import React, { useState } from "react";
import { Typography, Tag, Button, Skeleton, Tooltip } from "antd";
import { LuCalendar, LuImage, LuLink } from "react-icons/lu";
import CustomDivider from "../../commonComponents/divider";
import MediaPreview from "../../commonComponents/MediaPreview";
import { DATE_TIME_FORMATS } from "../../../utils/dateTimes";
import { convertUtcToLocal } from "../../../library/utils";
import {
  API_MEDIA_TYPE_MAP,
  LINK_DETAIL_FIELDS,
  MEDIA_TRACKING_STATUS,
  MEDIA_TRACKING_STATUS_COLOR,
  MEDIA_TYPE_DETAIL_FIELDS,
  getPreviewTypeFromApi,
} from "./mediaTrackingHelpers";

const { Text } = Typography;

const buildTitle = (mediaCount, linkType) => {
  // Non-media link types (e.g. APPOINTMENT) don't carry mediaCount — show a friendly label
  if (linkType && linkType !== "MEDIA") {
    return linkType.charAt(0) + linkType.slice(1).toLowerCase();
  }
  const counts = mediaCount || {};
  const parts = [];
  if (counts.image > 0) {
    parts.push(`${counts.image} Image${counts.image !== 1 ? "s" : ""}`);
  }
  if (counts.video > 0) {
    parts.push(`${counts.video} Video${counts.video !== 1 ? "s" : ""}`);
  }
  if (counts.document > 0) {
    parts.push(`${counts.document} Document${counts.document !== 1 ? "s" : ""}`);
  }
  return parts.join(" + ") || "Media";
};

const TimesSuffix = ({ count }) => (
  <span className="!text-[#52C41A] !font-normal !text-xs"> ({count} times)</span>
);

// Renders a "Yes" node plus an optional styled "(N times)" suffix when count > 1.
const renderYesWithCount = count => {
  const n = Number(count) || 0;
  return <>Yes{n > 1 && <TimesSuffix count={n} />}</>;
};

const renderFieldValue = (field, record) => {
  const rawValue = record?.[field.key];
  if (rawValue == null || rawValue === "") return null;

  if (field.isDate) return convertUtcToLocal(rawValue, DATE_TIME_FORMATS.MMM_D_YYYY_hh_mm_A);

  if (field.format === "openedCount") {
    const n = Number(rawValue) || 0;
    if (n <= 0) return null;
    return renderYesWithCount(n);
  }

  if (field.format === "yesnoWithCount") {
    if (!rawValue) return null;
    return renderYesWithCount(record?.[field.countKey]);
  }

  if (field.format === "yesno") return rawValue ? "Yes" : null;

  if (field.format === "seconds") {
    const n = Number(rawValue);
    return n > 0 ? `${n} sec` : null;
  }

  return rawValue;
};

const YES_LIKE_FORMATS = ["yesno", "yesnoWithCount", "openedCount"];

// Builds the MediaPreview carousel for a card: every tracked media with a URL,
// in the order the API returned them, so the preview matches the detail rows
// below it. `media_url` is the full-size asset; `thumbnail` is only the 48px
// header image, so it's never used as a slide.
const buildPreviewItems = trackingData =>
  (Array.isArray(trackingData) ? trackingData : [])
    .filter(record => record?.media_url)
    .map((record, index) => ({
      url: record.media_url,
      type: getPreviewTypeFromApi(record?.media_type),
      name: record?.file_name || `Media ${index + 1}`,
    }));

// Picks a fallback icon when no thumbnail URL is available
const getFallbackIcon = linkType => {
  if (linkType === "APPOINTMENT") return LuCalendar;
  if (linkType && linkType !== "MEDIA") return LuLink;
  return LuImage;
};

// 48x48 image with animated shimmer skeleton until the image fires onLoad.
// Uses native <img> for full control over the loaded state (Antd Image's placeholder
// often gets skipped on cached loads, hiding the skeleton effect).
const Thumbnail = ({ src }) => {
  const [loaded, setLoaded] = useState(false);
  return (
    <span className="relative inline-block w-12 h-12 rounded overflow-hidden flex-shrink-0">
      {!loaded && (
        <span
          className="absolute inset-0 animate-pulse"
          style={{
            background: "linear-gradient(90deg, #f0f0f0 0%, #f5f5f5 50%, #f0f0f0 100%)",
          }}
        />
      )}
      {/* eslint-disable-next-line jsx-a11y/alt-text */}
      <img
        src={src}
        width={48}
        height={48}
        loading="lazy"
        decoding="async"
        fetchPriority="low"
        alt=""
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
        className="absolute inset-0 w-full h-full object-cover"
      />
    </span>
  );
};

// Wraps the header thumbnail so it opens the previewer instead of toggling the
// card. Falls back to a plain span when there's nothing to preview, so a card
// with no media URL keeps the row's expand behaviour on the whole header.
const MediaThumbButton = ({ canPreview, onPreview, count, children }) => {
  // `?? null` so a missing child returns a valid empty render rather than undefined.
  if (!canPreview) return children ?? null;
  return (
    <Tooltip title={count > 1 ? `Preview ${count} media` : "Preview"}>
      <button
        type="button"
        aria-label="Preview media"
        className="!p-0 !border-0 !bg-transparent flex-shrink-0 cursor-pointer leading-none rounded hover:opacity-90"
        onClick={e => {
          e.stopPropagation();
          onPreview();
        }}
      >
        {children}
      </button>
    </Tooltip>
  );
};

const MediaTrackingCard = ({
  tracking,
  link,
  defaultExpanded = false,
  alwaysExpanded = false,
  showMethod = false,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded || alwaysExpanded);
  const isExpanded = alwaysExpanded || expanded;
  // Click-to-preview via the shared MediaPreview lightbox.
  const [previewOpen, setPreviewOpen] = useState(false);

  if (!tracking) return null;

  const title = buildTitle(tracking.mediaCount, tracking.linkType);
  const status = tracking.isOpened ? MEDIA_TRACKING_STATUS.OPENED : MEDIA_TRACKING_STATUS.SENT;
  const trackingData = Array.isArray(tracking.trackingData) ? tracking.trackingData : [];
  // First record with a thumbnail wins — otherwise we fall through to the default icon.
  const previewThumbnail = trackingData.find(r => r?.thumbnail)?.thumbnail || null;
  const rawMethod = trackingData[0]?.method;
  const isLinkMethod = rawMethod?.toUpperCase() === "LINK";
  const methodLabel = rawMethod ? (isLinkMethod ? "Link" : "MMS") : null;
  const methodMediaUrl = isLinkMethod ? trackingData[0]?.link || trackingData[0]?.media_url : null;
  // Not memoized on purpose: `if (!tracking) return null` above means a hook here
  // would run conditionally and break hook order. The arrays are a handful of
  // items, so recomputing costs nothing.
  const previewItems = buildPreviewItems(trackingData);
  const canPreview = previewItems.length > 0;

  return (
    <div
      className="flex flex-col"
      style={{
        // Browser skips paint + layout + image decode for cards that are off-screen.
        // contain-intrinsic-size reserves vertical space so the scrollbar stays accurate
        // (collapsed card ≈ 73px, expanded varies).
        contentVisibility: "auto",
        containIntrinsicSize: "auto 73px",
      }}
    >
      {/* Card Header */}
      <div
        className={`flex items-center gap-3 p-3 ${
          !alwaysExpanded ? "cursor-pointer hover:bg-[#F5F5F5]" : ""
        } transition-colors`}
        style={{
          backgroundColor: isExpanded ? "#F5F5F5" : "#FFFFFF",
          borderBottom: "1px solid #F0F0F0",
        }}
        onClick={() => !alwaysExpanded && setExpanded(prev => !prev)}
      >
        {/* The thumbnail opens the previewer; stopPropagation keeps it from also
            toggling the card, so clicking anywhere else still expands it. */}
        <MediaThumbButton
          canPreview={canPreview}
          onPreview={() => setPreviewOpen(true)}
          count={previewItems.length}
        >
          {previewThumbnail ? (
            <Thumbnail src={previewThumbnail} />
          ) : (
            <div className="flex items-center justify-center bg-[#E6F4FF] text-[#1677FF] rounded w-12 h-12 flex-shrink-0">
              {React.createElement(getFallbackIcon(tracking.linkType), { size: 20 })}
            </div>
          )}
        </MediaThumbButton>
        <div className="flex-1 min-w-0">
          <Text strong className="block text-sm">
            {title}
          </Text>
          {link?.url && (
            <Button
              type="link"
              className="p-0 h-auto text-xs"
              href={link.url}
              target="_blank"
              onClick={e => e.stopPropagation()}
            >
              {link.label}
            </Button>
          )}
        </div>
        {showMethod && methodLabel && (
          <span className="flex-shrink-0 text-xs text-center w-14">
            {isLinkMethod && methodMediaUrl ? (
              <a
                href={methodMediaUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={e => e.stopPropagation()}
                className="inline-flex items-center justify-center gap-1 text-[#1677FF] hover:underline"
              >
                <LuLink size={12} />
                {methodLabel}
              </a>
            ) : (
              <span className="text-[#000000E0]">{methodLabel}</span>
            )}
          </span>
        )}
        <Tag
          color={MEDIA_TRACKING_STATUS_COLOR[status]}
          className="flex-shrink-0 capitalize text-center"
          style={{ minWidth: 60, marginRight: 0 }}
        >
          {status?.toLowerCase()}
        </Tag>
      </div>

      {/* Detail View */}
      {isExpanded && (
        <div className="flex" style={{ backgroundColor: "#FFFFFF" }}>
          <div className="flex-shrink-0" style={{ width: 48, marginLeft: 12, marginRight: 12 }} />
          <div className="flex-1 py-2 pr-3">
            {/* Link-level fields — read directly from API tracking object */}
            {LINK_DETAIL_FIELDS.filter(field => !field.showWhen || field.showWhen(tracking)).map(
              field => {
                const displayValue = renderFieldValue(field, tracking);
                const isYes = YES_LIKE_FORMATS.includes(field.format) && displayValue != null;
                return (
                  <div key={field.key} className="flex py-1">
                    <Text className="!text-[13px] !flex-1 !leading-5 !text-[#00000099]">
                      {field.label}
                    </Text>
                    <Text
                      className={`!text-[13px] font-medium !flex-1 !leading-5 ${
                        isYes ? "!text-[#52C41A]" : "!text-[#000000E0]"
                      }`}
                    >
                      {displayValue !== null ? displayValue : "--"}
                    </Text>
                  </div>
                );
              }
            )}
            {/* Per-media rows — read directly from each trackingData record */}
            {trackingData.map((record, rIndex) => {
              const mediaTypeKey = API_MEDIA_TYPE_MAP[(record?.media_type || "").toLowerCase()];
              const fields = MEDIA_TYPE_DETAIL_FIELDS[mediaTypeKey] || [];
              if (fields.length === 0) return null;
              return (
                <div key={record?.mms_object_id ?? rIndex}>
                  <CustomDivider />
                  {fields.map(field => {
                    const displayValue = renderFieldValue(field, record);
                    const isYes = YES_LIKE_FORMATS.includes(field.format) && displayValue != null;
                    return (
                      <div key={field.key} className="flex py-1">
                        <Text className="!text-xs !flex-1 !leading-5 !text-[#00000073]">
                          {field.label}
                        </Text>
                        <Text
                          className={`!text-xs !flex-1 !leading-5 ${
                            isYes ? "!text-[#52C41A]" : "!text-[#000000E0]"
                          }`}
                        >
                          {displayValue !== null ? displayValue : "--"}
                        </Text>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Click-to-preview — all of this card's media as a carousel. Mounted only
          while open so an off-screen card carries no modal. */}
      {previewOpen && (
        <MediaPreview
          open={previewOpen}
          items={previewItems}
          title={title}
          showDownload={false}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </div>
  );
};

export function MediaTrackingCardSkeleton({ showDetails = true }) {
  return (
    <div className="flex flex-col">
      {/* Card Header Skeleton */}
      <div
        className="flex items-center gap-3 p-3"
        style={{
          backgroundColor: showDetails ? "#F5F5F5" : "#FFFFFF",
          borderBottom: "1px solid #F0F0F0",
        }}
      >
        <Skeleton.Avatar active shape="square" size={48} />
        <div className="flex-1">
          <Skeleton.Input active size="small" block style={{ height: 16, marginBottom: 6 }} />
          <Skeleton.Input active size="small" style={{ height: 14, width: 40 }} />
        </div>
        <Skeleton.Button active size="small" style={{ width: 50, height: 22 }} />
      </div>
      {/* Detail Skeleton */}
      {showDetails && (
        <div className="flex" style={{ backgroundColor: "#FFFFFF" }}>
          <div className="flex-shrink-0" style={{ width: 48, marginLeft: 12, marginRight: 12 }} />
          <div className="flex-1 py-2 pr-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="flex justify-between py-1">
                <Skeleton.Input active size="small" style={{ height: 14, width: 80 }} />
                <Skeleton.Input active size="small" style={{ height: 14, width: 120 }} />
              </div>
            ))}
            <CustomDivider />
            {[1, 2].map(i => (
              <div key={i} className="flex justify-between py-1">
                <Skeleton.Input active size="small" style={{ height: 14, width: 90 }} />
                <Skeleton.Input active size="small" style={{ height: 14, width: 60 }} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default React.memo(MediaTrackingCard);
